import React, { FC } from 'react';
import classNames from 'classnames';
import { makeStyles } from 'tss-react/mui';

import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';

import { transactionsTypes } from 'companion-app-components/flux/transactions';
import { tracker } from 'companion-app-components/utils/core';

import { isC2REnabledAccount } from 'data/accounts/utils';
import { isC2RTransaction } from 'data/transactions/utils';
import QTip from 'components/QuickenControls/QTip';
import AmountField from 'components/QuickenControls/AmountField';

import { getColorName } from '../utils';
import { RegListItem, styles } from '../styles';

import type { AttributesType, CallbacksType } from '../types';

const useStyles = makeStyles()(styles as Record<string, any>);

interface BalanceFieldProps {
  attributes: AttributesType;
  callbacks: CallbacksType;
  SHOW_ALL: boolean;
  txn: transactionsTypes.CashFlowTransaction;
  field: string;
  lastRow: boolean;
  enableAutoFocus: boolean;
  currencySymbol: string;
  showColor: boolean;
  hoverClass: string;
  bankPendingClass: string;
  isWindowsDataset: boolean;
  editable: boolean;
}

const BalanceField: FC<BalanceFieldProps> = ({
  attributes,
  callbacks,
  SHOW_ALL,
  txn,
  field,
  lastRow,
  enableAutoFocus,
  currencySymbol,
  showColor,
  bankPendingClass,
  isWindowsDataset,
  editable,
}) => {
  const { classes, theme } = useStyles();

  const showAcceptButton = isC2RTransaction(txn);

  const handleAccept = () => {
    if (!txn.isReviewed) {
      tracker.track(tracker.events.txnReview, {
        isC2REnabled: isWindowsDataset ? isC2REnabledAccount(txn.accountId) : false,
        action: 'accepted',
        count: 1,
      });
    }

    callbacks.handleMenu?.('reviewed', txn);
  };

  const renderAcceptButton = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Tooltip title="This transaction hasn’t been added to your account yet. Please review and ‘Accept’ to include it in your balance." placement="bottom">
        <Button
          onClick={handleAccept}
          className={classes.acceptBalanceButton}
          disabled={!editable}
        >
          ACCEPT
        </Button>
      </Tooltip>
    </Box>
  );

  const renderAmountField = () => (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {!lastRow && (txn.balance !== null && txn.balance !== undefined) ?
        // @ts-expect-error: missing props
        <QTip
          wrapId={`${txn.id}-${field}`}
        >
          <AmountField
            // @ts-expect-error: Property 'register' does not exist on type 'Components<BaseTheme>'.
            disableUnderline={theme.mixins?.register.noUnderline}
            clickable={false}
            thinFont
            amountType="balance"
            // @ts-expect-error: The expected type comes from property 'onChange' which is declared here on type 'IntrinsicAttributes & AmountFieldProps'
            onChange={callbacks.handleChange}
            onBlur={(e, type) => {
              callbacks.onAmountBlur?.(e, type);
            }}
            editable={false}
            autoFocus={enableAutoFocus}
            // @ts-expect-error: Type 'string' is not assignable to type 'number'.
            value={String(txn.balance)}
            color={getColorName(showColor, txn.balance)}
            className={classNames(classes.regInput, 'amount', classes.balanceAmount)}
            currencySymbol={currencySymbol}
          />
        </QTip> :
        ''}
    </>
  );

  const renderContent = () => showAcceptButton ? renderAcceptButton() : renderAmountField();

  return (
    <RegListItem
      {...attributes}
      tabIndex={null}
      className={classNames('amount', classes.regItem, bankPendingClass)}
    >
      {SHOW_ALL && renderContent()}
    </RegListItem>
  );
};

export default BalanceField;
